import { flattenObjectValues } from '@ohpen/react-utils';
import { FieldType, ProductResponse } from '@pmp/api';
import { ProductData } from '@pmp/types';
import { ProductCrudData } from '@pmp/components';
import { AdditionalFields, FormItem } from '@ohpen/enso-ui';
import { ExternalFiltersUrlCodec, PRODUCTS_ACTIVE_TODAY_PARAM, PRODUCTS_YEARS_PARAM } from '@pmp/utils';

import { ProductsListFilters } from './interfaces';

export const flattenProductAdditionalFields = (product: ProductResponse[]): ProductData[] => {
  return product.map(
    ({ additionalFields, ...productCoreData }) =>
      ({
        ...productCoreData,
        ...additionalFields,
      }) as ProductData,
  );
};

export const unflattenProductAdditionalFields = (product: ProductData[], additionalFieldsKeys: string[]): ProductResponse[] => {
  return product.map(row => {
    const additionalFields = additionalFieldsKeys.reduce(
      (acc, key) => {
        if (row[key] !== undefined) {
          acc[key] = row[key];
          delete row[key];
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    return {
      ...flattenObjectValues(row),
      additionalFields,
    };
  });
};

export const filterActiveToday = (products: ProductData[]): ProductData[] => {
  const today = new Date().setUTCHours(0, 0, 0, 0);

  return products.filter(product => {
    const startDate = product.startDate ? new Date(product.startDate).setUTCHours(0, 0, 0, 0) : null;
    const endDate = product.endDate ? new Date(product.endDate).setUTCHours(0, 0, 0, 0) : null;
    return (!startDate || startDate <= today) && (!endDate || endDate >= today);
  });
};

export const productsToProductsCrud = (products: ProductData[]): ProductCrudData[] => {
  return products.map((product, rowNumber) => {
    const clonedProduct: Record<string, any> = { ...product, rowNumber, originalCode: product.code };

    return clonedProduct as ProductCrudData;
  });
};

export const removeReplacesCode = (products: ProductCrudData[]): ProductCrudData[] => {
  return products.map(product => ({ ...product, replacesCode: null, lockedByActionId: null }) as ProductCrudData);
};

// Read off the originals rather than the copies removeReplacesCode makes of them, which have had this field
// cleared. A replacement gives the originals an end date, so a pending change request on any of them leaves
// the whole batch with no replacement to offer.
export const hasLockedProducts = (products: Pick<ProductData, 'lockedByActionId'>[]): boolean =>
  products.some(product => !!product.lockedByActionId);

const YEAR_PATTERN = /^\d{4}$/;

export const productsYearRange = (values: [string, string] | null | undefined): [string, string] | null => {
  if (!Array.isArray(values) || values.length !== 2) return null;
  return values.every(value => YEAR_PATTERN.test(value)) ? values : null;
};

export const productsRequestYears = (values: [string, string]): { startYear: number; endYear: number } => {
  const [first, second] = values.map(value => parseInt(value, 10));
  return { startYear: Math.min(first, second), endYear: Math.max(first, second) };
};

export const productsFiltersUrlCodec: ExternalFiltersUrlCodec<ProductsListFilters> = {
  toParams: filters => ({
    // Written even when it matches the current default, because that default follows the calendar: a
    // link without the years would show a different set of products next year. Omitted when the range
    // is not a pair of years, which `ProductsListFilters` forbids but a hand-edited or stale
    // `localStorage` entry can still produce; reading it back then falls through to the default.
    [PRODUCTS_YEARS_PARAM]: productsYearRange(filters.dates)?.join(','),
    [PRODUCTS_ACTIVE_TODAY_PARAM]: filters.activeToday ? undefined : 'false',
  }),
  fromParams: (params, defaults) => {
    const years = params.get(PRODUCTS_YEARS_PARAM)?.split(',');

    return {
      dates: productsYearRange(years?.length === 2 ? [years[0], years[1]] : null) ?? defaults.dates,
      activeToday: params.get(PRODUCTS_ACTIVE_TODAY_PARAM) !== 'false',
    };
  },
};

export const PRODUCTS_VALUE_LIST_NAMES: FieldType[] = [
  FieldType.ACTION_TYPE,
  FieldType.INITIAL_RATE_TYPE,
  FieldType.APPLICATION_REQUEST_TYPE,
  FieldType.PROPERTY_PURPOSE,
  FieldType.REPAYMENT_TYPE,
  FieldType.YES_NO,
  FieldType.BRANDS,
  FieldType.EMPLOYMENT_TYPE,
  FieldType.SCHEME,
  FieldType.OCCUPANCY_TYPE,
  FieldType.APPLICANT_TYPES,
  FieldType.PRODUCT_FEE_BASED_ON,
  FieldType.BUILDING_TYPE,
  FieldType.PROJECT_TYPE,
  FieldType.PROPERTY_TYPE,
  FieldType.REGULATORY_TYPE,
  FieldType.PRODUCT_TERM,
  FieldType.PROCURATION_FEES,
  FieldType.PROCURATION_FEE_RULES,
];

export const PRODUCTS_ENTITY_LIST_NAMES: FieldType[] = [
  FieldType.BASE_RATES,
  FieldType.FEE_PROFILES,
  FieldType.ERC_PROFILES,
  FieldType.EXCLUSIVITY_SETS,
];

export const getAdditionalFieldsSchema = (additionalFields: AdditionalFields): FormItem | null => {
  if (!additionalFields || Object.keys(additionalFields).length === 0) {
    return null;
  }

  return {
    title: 'pmp.products.labels.additionalFields',
    valueType: 'group',
    variant: 'card',
    collapsible: true,
    columns: Object.keys(additionalFields).map(key => ({
      title: key,
      dataIndex: key,
      valueType: 'text' as const,
    })),
  };
};
